import { useState } from 'react';
import { Check, X, Plus, Github, Trash2, Loader2 } from 'lucide-react';
import type { Repository } from '../types';
import { fetchRepoFromUrl } from '../utils/github';

interface EditableRepoCardProps {
  repo?: Repository;
  isAddNew?: boolean;
  isEditMode?: boolean;
  onSave: (repo: Repository) => void;
  onDelete?: (id: number) => void;
  onCancel?: () => void;
}

export default function EditableRepoCard({ 
  repo, 
  isAddNew = false, 
  isEditMode = false,
  onSave, 
  onDelete, 
  onCancel 
}: EditableRepoCardProps) {
  const [isEditing, setIsEditing] = useState(isAddNew);
  const [formData, setFormData] = useState<Repository>(
    repo || { 
      id: Date.now(), 
      name: '', 
      description: '', 
      html_url: '', 
      language: '', 
      stargazers_count: 0, 
      forks_count: 0, 
      fork: false, 
      topics: [] 
    }
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isFetching, setIsFetching] = useState(false);

  const isValidUrl = (url: string): boolean => {
    if (!url) return false;
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    // Only URL is required - name will be fetched from GitHub
    if (!formData.html_url.trim()) {
      newErrors.html_url = 'GitHub link is required';
    } else if (!isValidUrl(formData.html_url)) {
      newErrors.html_url = 'Please enter a valid GitHub URL';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Auto-fetch repo details when URL is entered
  const handleUrlChange = async (url: string) => {
    setFormData({ ...formData, html_url: url });
    if (errors.html_url) setErrors({ ...errors, html_url: '' });

    // Only fetch if it's a valid GitHub URL
    if (url.includes('github.com/') && url.split('/').length >= 5) {
      setIsFetching(true);
      try {
        const fetchedRepo = await fetchRepoFromUrl(url);
        if (fetchedRepo) {
          setFormData(prev => ({
            ...prev,
            name: fetchedRepo.name || prev.name,
            description: fetchedRepo.description || prev.description,
            language: fetchedRepo.language || prev.language,
            stargazers_count: fetchedRepo.stargazers_count || 0,
            forks_count: fetchedRepo.forks_count || 0,
          }));
        }
      } catch (error) {
        console.error('Failed to fetch repo:', error);
      } finally {
        setIsFetching(false);
      }
    }
  };

  const extractRepoName = (url: string): string => {
    // Extract repo name from GitHub URL if name is empty
    const match = url.match(/github\.com\/[^/]+\/([^/]+)/);
    return match ? match[1] : 'Unnamed Repo';
  };

  const handleSave = () => {
    if (!validate()) return;
    
    // Use fetched name, or extract from URL, or fallback
    const name = formData.name.trim() || extractRepoName(formData.html_url);
    
    const repoData: Repository = {
      ...formData,
      name,
      description: formData.description?.trim() || null,
      html_url: formData.html_url.trim(),
      language: formData.language?.trim() || null,
    };
    
    onSave(repoData);
    setIsEditing(false);
    if (isAddNew) {
      setFormData({ 
        id: Date.now(), 
        name: '', 
        description: '', 
        html_url: '', 
        language: '', 
        stargazers_count: 0, 
        forks_count: 0, 
        fork: false, 
        topics: [] 
      });
    }
  };

  const handleDelete = () => {
    if (repo && onDelete && window.confirm('Delete this repository?')) {
      onDelete(repo.id);
    }
  };

  const handleCancel = () => {
    if (isAddNew) {
      onCancel?.();
    } else {
      setFormData(repo || { 
        id: Date.now(), 
        name: '', 
        description: '', 
        html_url: '', 
        language: '', 
        stargazers_count: 0, 
        forks_count: 0, 
        fork: false, 
        topics: [] 
      });
      setIsEditing(false);
      setErrors({});
    }
  };

  if (isEditing) {
    return (
      <article className="card editing">
        <div className="card-dots">
          <span className="card-dot" />
          <span className="card-dot" />
          <span className="card-dot" />
        </div>
        <div className="card-content">
          <div className="edit-form">
            <input
              type="text"
              placeholder="Repository Name (auto-fetched from GitHub)"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
            
            <input
              type="text"
              placeholder="Description (optional)"
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value || null })}
            />
            
            <input
              type="text"
              placeholder="Language (optional, e.g., TypeScript)"
              value={formData.language || ''}
              onChange={(e) => setFormData({ ...formData, language: e.target.value })}
            />
            
            <div className="form-field">
              <div className={`input-with-icon ${isFetching ? 'fetching' : ''}`}>
                {isFetching ? (
                  <Loader2 size={16} className="input-icon loading-icon" />
                ) : (
                  <Github size={16} className="input-icon" />
                )}
                <input
                  type="url"
                  placeholder="GitHub URL * (auto-fetches info)"
                  value={formData.html_url}
                  onChange={(e) => handleUrlChange(e.target.value)}
                  className={errors.html_url ? 'error' : ''}
                />
              </div>
              {errors.html_url && <span className="field-error">{errors.html_url}</span>}
            </div>
            
            <div className="edit-actions">
              <button onClick={handleSave} className="btn-save">
                <Check size={16} /> Save
              </button>
              <button onClick={handleCancel} className="btn-cancel">
                <X size={16} /> Cancel
              </button>
            </div>
          </div>
        </div>
      </article>
    );
  }

  if (!repo) return null;

  return (
    <article className="card">
      <div className="card-dots">
        <span className="card-dot" />
        <span className="card-dot" />
        <span className="card-dot" />
      </div>
      <div className="card-content">
        {isEditMode && (
          <div className="edit-controls">
            <button onClick={handleDelete} className="btn-icon-delete" title="Delete">
              <Trash2 size={14} />
            </button>
          </div>
        )}
        <h3 className="card-title">{repo.name.toUpperCase()}</h3>
        <p className="card-subtitle">Repository</p>
        <p className="card-description">{repo.description || 'No description available.'}</p>
        
        {repo.language && (
          <div className="repo-language">
            <span className="language-dot" style={{ background: '#71717a' }} />
            <span>{repo.language}</span>
          </div>
        )}
        
        <a href={repo.html_url} target="_blank" rel="noopener noreferrer" className="card-link">
          <Github size={16} />
          <span>View Repository</span>
        </a>
      </div>
    </article>
  );
}

// Add new repo button/card
export function AddRepoButton({ onClick }: { onClick: () => void }) {
  return (
    <article className="card add-new-card" onClick={onClick}>
      <div className="card-dots">
        <span className="card-dot" />
        <span className="card-dot" />
        <span className="card-dot" />
      </div>
      <div className="card-content">
        <div className="add-new-content">
          <Plus size={32} />
          <span>Add Custom Repo</span>
        </div>
      </div>
    </article>
  );
}
